import { useEffect, useRef, useState } from 'react'
import { BackdropImage } from '@/components/primitives'
import type { NormalizedMenu } from '../menu/normalize'
import { useNavigator } from '../navigation/navigatorStore'
import { AmbientVideo } from './AmbientVideo'

/**
 * Pantalla inicial: una portada a pantalla completa por categoría.
 *
 * El `IntersectionObserver` marca cuál está activa para animar su fondo y
 * anunciarla a los lectores de pantalla. Se usa en lugar de escuchar el evento
 * `scroll` porque el observador no corre en el hilo principal en cada frame,
 * que es justo lo que haría que este scroll se sintiera pegajoso.
 */

export function CategoryExplorer({ menu }: { menu: NormalizedMenu }) {
  const enterCategory = useNavigator((s) => s.enterCategory)
  const [activeIndex, setActiveIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const index = Number((entry.target as HTMLElement).dataset.index)
          if (!Number.isNaN(index)) setActiveIndex(index)
        }
      },
      {
        root: container,
        // Solo cuenta como activa la portada que ocupa más de la mitad de la
        // pantalla: con un umbral bajo, dos categorías se disputarían el estado
        // a mitad de scroll.
        threshold: 0.55,
      },
    )

    for (const panel of container.querySelectorAll('[data-index]')) observer.observe(panel)
    return () => observer.disconnect()
  }, [])

  return (
    <div className="explorer" ref={containerRef}>
      {menu.categories.map((category, index) => {
        const subcategoryCount = category.subcategories.length
        const productCount = category.subcategories.reduce((sum, s) => sum + s.products.length, 0)

        return (
          <section
            key={category.id}
            className="category-panel"
            data-index={index}
            data-active={index === activeIndex}
            aria-label={category.name}
          >
            <BackdropImage
              image={category.image}
              fallback={category.fallback}
              className="category-panel__bg"
              // Solo la primera portada es candidata a LCP; precargarlas todas
              // saturaría la conexión antes de que se vea nada.
              eager={index === 0}
            />

            {/*
              El vídeo va ENCIMA de la foto, no en su lugar. Mientras no llega
              —o si no llega nunca— lo que se ve es la foto, sin hueco ni salto.
            */}
            <AmbientVideo video={category.video} active={index === activeIndex} />

            <div className="category-panel__scrim" aria-hidden="true" />

            <div className="category-panel__content">
              <p className="category-panel__index">
                {String(index + 1).padStart(2, '0')} /{' '}
                {String(menu.categories.length).padStart(2, '0')}
              </p>
              <h2 className="category-panel__name">{category.name}</h2>

              <p className="category-panel__meta">
                <span>
                  {subcategoryCount} {subcategoryCount === 1 ? 'sección' : 'secciones'}
                </span>
                <span aria-hidden="true">·</span>
                <span>
                  {productCount} {productCount === 1 ? 'plato' : 'platos'}
                </span>
              </p>

              {/* Un único botón: es el destino del foco por teclado y lo que
                  anuncia el lector de pantalla. El texto visible dice solo
                  "Explorar" porque el nombre de la categoría está justo encima;
                  el aria-label lo repite para quien no ve esa relación
                  espacial. Duplicar el botón haría que se anunciara dos veces. */}
              <button
                type="button"
                className="button"
                style={{ marginTop: '1.1rem', maxWidth: '16rem' }}
                aria-label={`Explorar ${category.name}`}
                onClick={() => enterCategory(index)}
              >
                Explorar
              </button>
            </div>
          </section>
        )
      })}
    </div>
  )
}
